import React, { useEffect, useState, useRef } from 'react';
import { ArrowUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export interface FooterLink {
  href: string;
  label: string;
}
interface AnimatedFooterProps {
  leftLinks: FooterLink[];
  rightLinks: FooterLink[];
  copyrightText: string;
  barCount?: number;
}
export function AnimatedFooter({
  leftLinks,
  rightLinks,
  copyrightText,
  barCount = 23
}: AnimatedFooterProps) {
  const waveRefs = useRef<Array<HTMLDivElement | null>>([]);
  const footerRef = useRef<HTMLElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      threshold: 0.15
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!isVisible || prefersReducedMotion) return;
    let time = 0;
    const animateWave = () => {
      let offset = 0;
      waveRefs.current.forEach((element, index) => {
        if (!element) return;
        offset += Math.max(0, 13 * Math.sin((time + index * 0.8) * 0.32));
        element.style.transform = `translateY(${index + offset}px)`;
      });
      time += 0.065;
      animationFrameRef.current = requestAnimationFrame(animateWave);
    };
    animateWave();
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isVisible, prefersReducedMotion]);
  return <footer ref={footerRef} className="relative flex min-h-[680px] w-full select-none flex-col justify-between overflow-hidden border-t border-white/10 bg-black text-white lg:min-h-screen" data-id="element-118" data-mp="3g982c2crou69">
      <div className="container relative z-10 mx-auto grid w-full gap-12 px-6 pb-16 pt-10 md:grid-cols-[1fr_auto] md:px-12 md:pt-12" data-id="element-119" data-mp="3697eq47v7rw9">
        <div data-id="element-120" data-mp="2wjxo6sso6ha9">
          <p className="text-[11px] font-semibold tracking-[0.22em] text-white" data-id="element-121" data-mp="2wk60aigyxike">
            GROWTHLY
          </p>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-500" data-id="element-122" data-mp="31aw4hsdnrfdi">
            Diseño y presencia digital para negocios que quieren verse tan bien
            como trabajan.
          </p>
          <nav className="mt-8" aria-label="Enlaces legales" data-id="element-123" data-mp="2shj7ofch3tm5">
            <ul className="flex flex-wrap gap-x-5 gap-y-3" data-id="element-124" data-mp="2dl1urjomy6xh">
              {leftLinks.map(link => <li key={link.href} data-id="element-125" data-mp="2ik4yn0svq73v">
                  <Link to={link.href} className="text-xs text-neutral-400 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-126" data-mp="2ij3du4m2a68y">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
          <p className="mt-8 text-xs text-neutral-600" data-id="element-127" data-mp="23xfs4f07mtnz">{copyrightText}</p>
        </div>
        <div className="md:text-right" data-id="element-128" data-mp="23zsgpb0h42a0">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-neutral-600" data-id="element-129" data-mp="2hwff7i82r0i1">
            Navegación
          </p>
          <nav className="mt-5" aria-label="Navegación del pie de página" data-id="element-130" data-mp="2dv3w7vjphdit">
            <ul className="flex max-w-sm flex-wrap gap-x-5 gap-y-3 md:justify-end" data-id="element-131" data-mp="2rlz00tfcl4fj">
              {rightLinks.map(link => <li key={link.href} data-id="element-132" data-mp="2mopbdfzs8wsr">
                  <Link to={link.href} className="text-sm text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-133" data-mp="31ky9ge7xt43l">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
          <button type="button" onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })} className="mt-8 inline-flex items-center gap-2 text-xs text-neutral-400 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-134" data-mp="ywq0wpln43kd">
            Volver arriba <ArrowUpIcon size={14} aria-hidden="true" data-id="element-135" data-mp="g1rgh9agz4dr" />
          </button>
        </div>
      </div>

      <div className="pointer-events-none relative h-[260px] overflow-hidden sm:h-[320px]" aria-hidden="true" data-id="element-136" data-mp="l0un2y63linu">
        <div className="absolute inset-x-0 bottom-0" data-id="element-137" data-mp="1weg8q9cr124m">
          {Array.from({
          length: barCount
        }).map((_, index) => <div key={index} ref={element => {
          waveRefs.current[index] = element;
        }} className="bg-white motion-reduce:translate-y-0" style={{
          height: `${index + 1}px`,
          marginTop: '-2px',
          willChange: 'transform'
        }} data-id="element-138" data-mp="21bggcop8ycxu" data-mpi={index} />)}
        </div>
      </div>
    </footer>;
}